/**
 * A leitura da timeline unificada (H08).
 *
 * ── A armadilha é separar por abas ────────────────────────────────────────
 *
 * *"O valor está em ver a sequência — que a mensagem veio depois da mudança de
 * etapa e antes da tarefa. Separado, o usuário reconstrói isso de cabeça."*
 *
 * Uma aba por tipo parece organização e é devolução de trabalho: quem lê passa a
 * fazer o que o produto deveria ter feito. Por isso a lista é uma só, e o filtro
 * por tipo é subtrativo — serve para tirar ruído de uma sequência que continua
 * sendo sequência, não para trocar de lista.
 *
 * ── Agrupar por dia é diferente de agrupar por tipo ───────────────────────
 *
 * O dia não quebra a sequência: dentro dele a ordem cronológica continua, e
 * entre eles também. O que ele faz é dar régua — "isso foi na terça" é como as
 * pessoas se localizam num histórico de dois anos.
 */

import type { ItemDaTimeline, TipoDeItemDaTimeline } from '@cliqcrm/contracts';
import type { NomeDoIcone } from '@/shared/ui';
import { nomeDaEtapa } from '@/entities/comercial/etapa';

/**
 * O nome de cada tipo na tela, e o que ele significa.
 *
 * ── `nota` passou a ter origem, e a história vale ─────────────────────────
 *
 * Ela vinha marcada como "sem origem" e voltava vazia, e o motivo era uma
 * confusão de nomes: o tipo `nota` da timeline é a nota do CRM, que nenhuma
 * origem escreve. Só que a nota que a equipe de fato escreve é OUTRA coisa —
 * é a nota interna da conversa, uma mensagem privada.
 *
 * Quem abria o filtro via "Notas — sem origem" e concluía que o produto não
 * guardava as notas dele. Guardava; estavam na conversa. Agora a rota as lê de
 * lá, e o filtro cumpre o que promete.
 *
 * `evento_de_portal` continua sem origem: os conectores são a etapa E. Fica no
 * filtro assim mesmo, marcado, porque esconder o tipo faria a ausência parecer
 * defeito.
 */
export const TIPOS: readonly {
  readonly tipo: TipoDeItemDaTimeline;
  /** O rótulo do FILTRO, no plural: ele liga ou desliga uma categoria. */
  readonly nome: string;
  /**
   * O rótulo de UM evento, no singular.
   *
   * A linha da tabela diz o que aconteceu uma vez — "Mensagens" e "Tarefas"
   * numa fileira que descreve um único fato lê como contagem, não como nome.
   */
  readonly unidade: string;
  /**
   * O ícone do conjunto de traço, e não um emoji.
   *
   * Era `💬 ↗ ✓ ✎ ⇩`. Emoji renderiza diferente em cada sistema, não herda
   * `currentColor` — então não acompanha o tema escuro nem a cor do disco do
   * evento — e o peso óptico varia de fonte para fonte.
   */
  readonly sinal: NomeDoIcone;
  /** `false` = existe no contrato e ainda não tem origem. */
  readonly alimentado: boolean;
}[] = [
  {
    tipo: 'mensagem',
    nome: 'Mensagens',
    unidade: 'Mensagem',
    sinal: 'atendimento',
    alimentado: true,
  },
  {
    tipo: 'mudanca_de_etapa',
    nome: 'Mudança de etapa',
    unidade: 'Mudança de etapa',
    sinal: 'funil',
    alimentado: true,
  },
  { tipo: 'tarefa', nome: 'Tarefas', unidade: 'Tarefa', sinal: 'tarefas', alimentado: true },
  {
    tipo: 'nota',
    nome: 'Notas internas',
    unidade: 'Nota interna',
    sinal: 'nota',
    alimentado: true,
  },
  {
    tipo: 'encerramento',
    nome: 'Encerramentos',
    unidade: 'Conversa encerrada',
    sinal: 'atendimento',
    alimentado: true,
  },
  {
    tipo: 'evento_de_portal',
    nome: 'Portais',
    unidade: 'Evento de portal',
    sinal: 'portal',
    alimentado: false,
  },
];

const POR_TIPO = new Map(TIPOS.map((t) => [t.tipo, t] as const));

/**
 * O nome de UM evento, no singular — é o que a fileira da tabela mostra.
 *
 * O plural é do filtro, que liga ou desliga uma categoria inteira. Reaproveitá-lo
 * aqui fazia cada linha dizer "Mensagens" e "Tarefas" sobre um fato único, e
 * isso lê como contagem em vez de nome.
 */
export function nomeDoTipo(tipo: string): string {
  return POR_TIPO.get(tipo as TipoDeItemDaTimeline)?.unidade ?? tipo;
}

export function sinalDoTipo(tipo: string): NomeDoIcone {
  // `nota` como padrão: um tipo que a tela não conhece ainda é, no fim, um
  // registro escrito. Melhor que um ponto sem forma no meio da corrente.
  return POR_TIPO.get(tipo as TipoDeItemDaTimeline)?.sinal ?? 'nota';
}

export interface DiaDaTimeline {
  /** `2026-08-18` — a chave de agrupamento, em dia LOCAL. */
  readonly chave: string;
  readonly rotulo: string;
  readonly itens: readonly ItemDaTimeline[];
}

/**
 * Agrupa por dia do calendário local, preservando a ordem recebida.
 *
 * A chave é montada dos componentes locais, e não de `toISOString().slice(0,10)`:
 * o ISO é UTC, e um fato das 22h de terça em São Paulo cairia na quarta. O
 * histórico ficaria com o divisor no lugar errado — e é o tipo de erro que só
 * aparece à noite.
 */
export function agruparPorDia(
  itens: readonly ItemDaTimeline[],
  agora = Date.now(),
): readonly DiaDaTimeline[] {
  const dias: DiaDaTimeline[] = [];

  for (const item of itens) {
    const data = new Date(item.ocorrido_em);
    if (Number.isNaN(data.getTime())) continue;

    const chave = chaveDoDia(data);
    const ultimo = dias[dias.length - 1];

    if (ultimo?.chave === chave) {
      (ultimo.itens as ItemDaTimeline[]).push(item);
    } else {
      dias.push({ chave, rotulo: rotuloDoDia(data, agora), itens: [item] });
    }
  }

  return dias;
}

function chaveDoDia(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

function rotuloDoDia(data: Date, agora: number): string {
  const hoje = new Date(agora);
  const ontem = new Date(agora);
  ontem.setDate(ontem.getDate() - 1);

  if (chaveDoDia(data) === chaveDoDia(hoje)) return 'Hoje';
  if (chaveDoDia(data) === chaveDoDia(ontem)) return 'Ontem';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    ...(data.getFullYear() === hoje.getFullYear() ? {} : { year: 'numeric' }),
  }).format(data);
}

/**
 * O atalho de cada item da timeline.
 *
 * `referencia` é aberto de propósito no contrato — cada tipo aponta o que faz
 * sentido para ele. A leitura é defensiva pelo mesmo motivo das capabilities:
 * tipo novo com referência diferente não pode derrubar a tela.
 */
export function conversaDoItem(item: ItemDaTimeline): string | null {
  return item.conversa_id;
}

/**
 * A mudança de etapa em uma frase, com os NOMES das etapas.
 *
 * ── O que isto substitui ──────────────────────────────────────────────────
 *
 * Havia um `referenciaLegivel` que despejava o mapa `referencia` inteiro como
 * texto, chave a chave:
 *
 *     etapa: MEETING · etapa_anterior: SCREENING · opportunity_source_id: bef94…
 *
 * Nada ali é legível para quem usa: `opportunity_source_id` é id de motor,
 * `etapa_anterior` é nome de campo nosso, e `MEETING` é o código gravado, não o
 * nome que a revenda deu à coluna. A linha aparecia embaixo de TODO evento de
 * etapa, e a informação útil — de onde para onde — já estava no `trecho`.
 *
 * Agora `referencia` é lida como DADO, e não impressa como texto: dela saem as
 * duas etapas, e `nomeDaEtapa` as traduz para o que se lê no quadro.
 */
export function mudancaDeEtapaEmPalavras(item: ItemDaTimeline): string | null {
  if (item.tipo !== 'mudanca_de_etapa') return null;

  // O nome que o servidor manda pronto (com o funil, quando há vários); sem ele, o valor.
  const de =
    texto(item.referencia['etapa_anterior_nome']) ?? texto(item.referencia['etapa_anterior']);
  const para = texto(item.referencia['etapa_nome']) ?? texto(item.referencia['etapa']);
  if (para === null) return null;

  return de === null
    ? `Entrou em ${nomeDaEtapa(para)}`
    : `De ${nomeDaEtapa(de)} para ${nomeDaEtapa(para)}`;
}

/** Só texto não vazio; qualquer outra coisa é ausência. */
function texto(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}
